import { useMemo, useRef, type PointerEvent } from 'react';
import type { Channel, KeyConfig, Swatch } from '@bccomms/shared';
import { parseTarget } from '@bccomms/shared';
import { session } from '../lib/session';
import { targetLabel, useStore } from '../store';

/**
 * Unity-style dial: top arc = TALK keys (arm several, then hold / latch the centre button),
 * bottom arc = LISTEN keys, program (PGM) keys sit apart at the right end of each arc.
 */
const SIZE = 800;
const C = SIZE / 2;
const R_OUT = 350;
const R_IN = 272;
const R_BTN = 182;
const TOP = { start: 187, end: 353 }; // SVG degrees, clockwise from +x; passes 270° (top)
const BOT = { start: 7, end: 173 }; // passes 90° (bottom)
const GAP = 1.8;
const PGM_GAP = 9;
const TAP_MS = 250;
const LONG_MS = 550;

const polar = (r: number, deg: number): [number, number] => {
  const a = (deg * Math.PI) / 180;
  return [C + r * Math.cos(a), C + r * Math.sin(a)];
};

function sectorPath(a0: number, a1: number, rIn: number, rOut: number): string {
  const [x0, y0] = polar(rOut, a0);
  const [x1, y1] = polar(rOut, a1);
  const [x2, y2] = polar(rIn, a1);
  const [x3, y3] = polar(rIn, a0);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M${x0.toFixed(1)} ${y0.toFixed(1)} A${rOut} ${rOut} 0 ${large} 1 ${x1.toFixed(1)} ${y1.toFixed(1)} L${x2.toFixed(1)} ${y2.toFixed(1)} A${rIn} ${rIn} 0 ${large} 0 ${x3.toFixed(1)} ${y3.toFixed(1)} Z`;
}

function arcPath(a0: number, a1: number, r: number): string {
  const [x0, y0] = polar(r, a0);
  const [x1, y1] = polar(r, a1);
  return `M${x0.toFixed(1)} ${y0.toFixed(1)} A${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${x1.toFixed(1)} ${y1.toFixed(1)}`;
}

interface Slot {
  key: KeyConfig;
  a0: number;
  a1: number;
  isProgram: boolean;
}

/** Lay keys out along an arc; program keys are grouped at the right end with a wider gap. */
function layout(keys: KeyConfig[], programIds: Set<string>, arc: { start: number; end: number }, bottom: boolean): Slot[] {
  const normal = keys.filter((k) => !programIds.has(k.id));
  const pgm = keys.filter((k) => programIds.has(k.id));
  // Top arc runs left→right; bottom arc runs right→left (increasing angle), so reverse there.
  const ordered = bottom ? [...pgm, ...[...normal].reverse()] : [...normal, ...pgm];
  const n = ordered.length;
  if (!n) return [];
  const sweep = arc.end - arc.start;
  const extra = pgm.length && normal.length ? PGM_GAP - GAP : 0;
  const seg = (sweep - (n - 1) * GAP - extra) / n;
  const out: Slot[] = [];
  let a = arc.start;
  ordered.forEach((key, i) => {
    const isProgram = programIds.has(key.id);
    const prev = ordered[i - 1];
    if (i > 0 && prev && programIds.has(prev.id) !== isProgram) a += extra;
    out.push({ key, a0: a, a1: a + seg, isProgram });
    a += seg + GAP;
  });
  return out;
}

function keyLabel(key: KeyConfig, channels: Channel[]): string {
  if (key.label) return key.label.toUpperCase().slice(0, 7);
  if (key.targets.length > 1) return 'GRP';
  const p = parseTarget(key.targets[0]);
  if (p?.kind === 'ch') return channels.find((c) => c.id === p.id)?.short ?? '?';
  return targetLabel(key.targets[0], true);
}

function keySwatch(key: KeyConfig, channels: Channel[], users: { id: string; color: Swatch }[]): Swatch {
  if (key.color) return key.color;
  const p = parseTarget(key.targets[0]);
  if (p?.kind === 'ch') return channels.find((c) => c.id === p.id)?.color ?? 'cyan';
  return users.find((u) => u.id === p?.id)?.color ?? 'cyan';
}

export function Dial() {
  const panel = useStore((s) => s.panel);
  const channels = useStore((s) => s.channels);
  const users = useStore((s) => s.users);
  const me = useStore((s) => s.user);
  const presence = useStore((s) => s.presence);
  const talking = useStore((s) => s.talking);
  const listening = useStore((s) => s.listening);
  const armed = useStore((s) => s.armed);
  const continuous = useStore((s) => s.continuous);
  const centerHeld = useStore((s) => s.centerHeld);
  const feeding = useStore((s) => s.feeding);
  const calls = useStore((s) => s.calls);
  const media = useStore((s) => s.media);

  const programIds = useMemo(() => {
    const programChannels = new Set(channels.filter((c) => c.kind === 'program').map((c) => `ch:${c.id}`));
    return new Set((panel?.keys ?? []).filter((k) => k.targets.length === 1 && programChannels.has(k.targets[0])).map((k) => k.id));
  }, [panel, channels]);

  const talkKeys = useMemo(
    () => (panel?.keys ?? []).filter((k) => k.talk !== 'none' && (!programIds.has(k.id) || me?.canFeedProgram)),
    [panel, programIds, me?.canFeedProgram],
  );
  const listenKeys = useMemo(() => (panel?.keys ?? []).filter((k) => k.listen !== 'none'), [panel]);
  const top = useMemo(() => layout(talkKeys, programIds, TOP, false), [talkKeys, programIds]);
  const bottom = useMemo(() => layout(listenKeys, programIds, BOT, true), [listenKeys, programIds]);

  const now = Date.now();
  const armedTargets = session.armedTargets();
  const centerOn = (continuous || centerHeld) && armedTargets.length > 0;
  const tx = talking.length > 0;

  // Incoming talkers per target.
  const talkersByTarget = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const p of Object.values(presence)) {
      if (!p.online || p.userId === me?.id) continue;
      const name = users.find((u) => u.id === p.userId)?.displayName ?? '?';
      for (const t of p.talking) {
        const list = map.get(t) ?? [];
        list.push(name);
        map.set(t, list);
      }
    }
    return map;
  }, [presence, me?.id, users]);

  const incomingOn = (key: KeyConfig): string[] =>
    key.targets.flatMap((t) => {
      const p = parseTarget(t);
      if (p?.kind === 'user') return talkersByTarget.get(`user:${me?.id}`)?.filter(() => presence[p.id]?.talking.includes(`user:${me?.id}`)) ?? [];
      return listening.includes(t) ? (talkersByTarget.get(t) ?? []) : [];
    });

  const programFeeders = (key: KeyConfig): boolean => {
    const p = parseTarget(key.targets[0]);
    if (p?.kind !== 'ch') return false;
    const ch = channels.find((c) => c.id === p.id);
    const pid = ch?.kind === 'ifb' ? ch.programChannelId : ch?.kind === 'program' ? ch.id : null;
    return !!pid && Object.values(presence).some((x) => x.online && x.feeding.includes(pid));
  };

  // ---- pointer handling for segments (tap = toggle, long-press on talk = call) ----
  const longTimer = useRef<number | null>(null);
  const longFired = useRef(false);
  const segDown = (key: KeyConfig, side: 'talk' | 'listen') => (e: PointerEvent<SVGGElement>) => {
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    longFired.current = false;
    if (side === 'talk' && !programIds.has(key.id)) {
      longTimer.current = window.setTimeout(() => {
        longFired.current = true;
        if (navigator.vibrate) navigator.vibrate(40);
        void session.call(key);
      }, LONG_MS);
    }
  };
  const segUp = (key: KeyConfig, side: 'talk' | 'listen') => (e: PointerEvent<SVGGElement>) => {
    e.preventDefault();
    if (longTimer.current) clearTimeout(longTimer.current);
    longTimer.current = null;
    if (longFired.current) return;
    if (side === 'talk') {
      if (programIds.has(key.id)) {
        const p = parseTarget(key.targets[0]);
        if (p) void session.toggleProgram(p.id);
      } else void session.setArmed(key, !armed.includes(key.id));
    } else if (key.listen === 'toggle') {
      const on = key.targets.every((t) => listening.includes(t));
      void session.listen(key, !on);
    }
  };
  const segCancel = () => {
    if (longTimer.current) clearTimeout(longTimer.current);
    longTimer.current = null;
  };

  // ---- centre button: hold = PTT, tap = continuous toggle ----
  const downAt = useRef(0);
  const btnDown = (e: PointerEvent<SVGGElement>) => {
    e.preventDefault();
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    downAt.current = performance.now();
    if (!continuous) void session.center(true);
  };
  const btnUp = (e: PointerEvent<SVGGElement>) => {
    e.preventDefault();
    const held = performance.now() - downAt.current;
    if (held < TAP_MS) {
      void session.setContinuous(!continuous);
      if (!continuous) return; // now continuous: keep talking
      void session.center(false);
      return;
    }
    if (!continuous) void session.center(false);
  };

  const renderSeg = (slot: Slot, side: 'talk' | 'listen') => {
    const { key, a0, a1 } = slot;
    const mid = (a0 + a1) / 2;
    const [lx, ly] = polar((R_IN + R_OUT) / 2, mid);
    const rot = side === 'talk' ? mid + 90 : mid - 90;
    const sw = keySwatch(key, channels, users);
    const label = keyLabel(key, channels);
    const isProgram = slot.isProgram;
    const p = parseTarget(key.targets[0]);
    const chId = p?.kind === 'ch' ? p.id : null;
    const call = key.targets.some((t) => (calls[t] ?? 0) > now);
    const cls = ['seg'];
    if (isProgram) cls.push('seg--pgm');
    if (side === 'talk') {
      if (isProgram) {
        if (chId && feeding.includes(chId)) cls.push('seg--armed', 'seg--tx');
      } else {
        if (armed.includes(key.id)) cls.push('seg--armed');
        if (key.targets.some((t) => talking.includes(t))) cls.push('seg--tx');
      }
    } else {
      const on = key.listen === 'always' || key.targets.every((t) => listening.includes(t));
      if (on) cls.push('seg--listen');
      if (key.listen === 'always') cls.push('seg--locked');
      if (incomingOn(key).length) cls.push('seg--in');
      if (key.listen === 'none') cls.push('seg--disabled');
    }
    if (call) cls.push('seg--call');
    const feederDot = side === 'listen' && programFeeders(key);
    return (
      <g
        key={`${side}-${key.id}`}
        className={cls.join(' ')}
        data-sw={sw}
        onPointerDown={segDown(key, side)}
        onPointerUp={segUp(key, side)}
        onPointerCancel={segCancel}
        onContextMenu={(e) => e.preventDefault()}
        role="button"
        aria-label={`${side} ${label}`}
      >
        <path className="seg__body" d={sectorPath(a0, a1, R_IN, R_OUT)} />
        <path className="seg__edge" d={arcPath(a0 + 0.6, a1 - 0.6, R_OUT - 2)} />
        <text className="seg__label" x={lx} y={ly} transform={`rotate(${rot} ${lx} ${ly})`}>
          {label}
        </text>
        {feederDot && <circle cx={polar(R_IN + 14, mid)[0]} cy={polar(R_IN + 14, mid)[1]} r={5} fill="var(--lime-500)" />}
        {side === 'talk' && !isProgram && key.vox && (
          <text className="seg__vox" x={polar(R_OUT - 14, mid)[0]} y={polar(R_OUT - 14, mid)[1]} transform={`rotate(${rot} ${polar(R_OUT - 14, mid)[0]} ${polar(R_OUT - 14, mid)[1]})`}>
            VOX
          </text>
        )}
      </g>
    );
  };

  const inNow = useMemo(() => {
    const out: string[] = [];
    for (const [t, names] of talkersByTarget) {
      if (t === `user:${me?.id}` || listening.includes(t)) for (const n of names) out.push(`${n} → ${targetLabel(t, true)}`);
    }
    return out;
  }, [talkersByTarget, listening, me?.id]);

  if (!panel || panel.keys.length === 0) {
    return (
      <div className="empty">
        <h3>No keys assigned</h3>
        <p>An administrator needs to assign a panel to {me?.displayName ?? 'you'}.</p>
      </div>
    );
  }

  const btnCls = ['dial__btn', centerOn && tx ? 'dial__btn--tx' : '', armedTargets.length === 0 ? 'dial__btn--idle' : ''].join(' ');

  return (
    <div className="dial-wrap">
      <svg className="dial" viewBox={`0 0 ${SIZE} ${SIZE}`} role="group" aria-label="Intercom dial">
        <defs>
          <linearGradient id="equator" x1="0" x2="1">
            <stop offset="0" stopColor="#ffffff" stopOpacity={0} />
            <stop offset="0.12" stopColor="#ffffff" stopOpacity={0.6} />
            <stop offset="0.88" stopColor="#ffffff" stopOpacity={0.6} />
            <stop offset="1" stopColor="#ffffff" stopOpacity={0} />
          </linearGradient>
        </defs>
        <text className="dial__caption" x={C} y={34} textAnchor="middle">
          TALK
        </text>
        <text className="dial__caption" x={C} y={SIZE - 10} textAnchor="middle">
          LISTEN
        </text>
        <line className="dial__equator" x1={30} y1={C} x2={SIZE - 30} y2={C} stroke="url(#equator)" strokeWidth={5} strokeLinecap="round" />
        {top.map((s) => renderSeg(s, 'talk'))}
        {bottom.map((s) => renderSeg(s, 'listen'))}
        <g className={btnCls} onPointerDown={btnDown} onPointerUp={btnUp} onPointerCancel={() => !continuous && void session.center(false)} onContextMenu={(e) => e.preventDefault()} role="button" aria-pressed={centerOn}>
          <circle className="dial__btn__disc" cx={C} cy={C} r={R_BTN} />
          {continuous && (
            <text className="dial__btn__small" x={C} y={C - 96}>
              CONTINUOUS
            </text>
          )}
          <text className="dial__btn__big" x={C} y={C - 4} style={{ fontSize: armedTargets.length === 0 ? 118 : 150 }}>
            {armedTargets.length === 0 ? 'SELECT' : 'TALK'}
          </text>
          <text className="dial__btn__small" x={C} y={C + 78}>
            {armedTargets.length === 0 ? 'A TALK KEY' : continuous ? 'MODE · TAP TO END' : centerHeld ? 'ON AIR' : 'HOLD · TAP = LATCH'}
          </text>
          <text className="dial__btn__targets" x={C} y={C + 128}>
            {armedTargets.map((t) => targetLabel(t, true)).join(' · ')}
          </text>
        </g>
      </svg>
      <div className="dial-status">
        {media === 'nomic' && <span className="chip chip--warning">NO MIC · listen only</span>}
        {inNow.map((s) => (
          <span key={s} className="chip chip--success">
            {s}
          </span>
        ))}
        {feeding.length > 0 && <span className="chip chip--brand">FEEDING PGM</span>}
      </div>
      <div className="dial-hint">Tap a talk key to arm it · hold centre to talk · tap centre for continuous · long-press a talk key to call · volumes in the mixer</div>
    </div>
  );
}
